import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import {
  ClipboardList,
  Clock,
  ShieldAlert,
  FileText,
  Receipt,
  AlertTriangle,
  Flame,
  Package,
  Wrench,
  Hammer,
  Fuel,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each tile maps to a form template by name match. We resolve the template_id
// at runtime so the tile links straight to /dashboard/forms/fill/<id> instead
// of dropping the user onto the generic forms hub.
const MEMBER_TILES = [
  {
    label: 'My Timesheet',
    description: 'Submit a timesheet entry',
    matchName: 'timesheet entry',
    icon: Clock,
    color: 'bg-emerald-600',
    testId: 'member-tile-timesheet',
  },
  {
    label: 'Shop Timesheet',
    description: 'Submit a shop timesheet',
    matchName: 'shop time sheet',
    icon: Hammer,
    color: 'bg-emerald-700',
    testId: 'member-tile-shop-timesheet',
  },
  {
    label: 'Time Off Request',
    description: 'Request time off',
    matchName: 'time-off request',
    icon: ClipboardList,
    color: 'bg-blue-600',
    testId: 'member-tile-timeoff',
  },
  {
    label: 'FLHA',
    description: 'Field Level Hazard Assessment',
    matchName: 'field level hazard',
    icon: ShieldAlert,
    color: 'bg-orange-600',
    testId: 'member-tile-flha',
  },
  {
    label: 'BOL',
    description: 'Bill of Lading',
    matchName: 'bill of lading',
    icon: FileText,
    color: 'bg-purple-600',
    testId: 'member-tile-bol',
  },
  {
    label: 'Scale Receipt',
    description: 'Submit a scale receipt',
    matchName: 'scale receipt',
    icon: Receipt,
    color: 'bg-cyan-600',
    testId: 'member-tile-scale',
  },
  {
    label: 'ClearSky Fuel Log',
    description: 'Record diesel + ClearSky additive',
    matchName: 'clearsky',
    icon: Fuel,
    color: 'bg-emerald-500',
    testId: 'member-tile-clearsky',
  },
  {
    label: 'Spill Report',
    description: 'Report a spill incident',
    matchName: 'spill',
    icon: Flame,
    color: 'bg-red-600',
    testId: 'member-tile-spill',
  },
  {
    label: 'Incident Report',
    description: 'Report an incident',
    matchName: 'incident',
    icon: AlertTriangle,
    color: 'bg-amber-600',
    testId: 'member-tile-incident',
  },
  {
    label: 'My Submissions',
    description: 'See your filed forms',
    href: '/dashboard/forms/submissions',
    icon: Package,
    color: 'bg-teal-600',
    testId: 'member-tile-submissions',
  },
  {
    label: 'Shop Orders',
    description: 'Shop work order checklist',
    href: '/dashboard/shop-orders',
    icon: Wrench,
    color: 'bg-slate-600',
    testId: 'member-tile-shop-orders',
  },
];

export default function MemberDashboardPage() {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const [templatesByName, setTemplatesByName] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('vaultai_token');
    axios
      .get(`${API}/forms/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : res.data?.templates || res.data?.items || [];
        const map = {};
        items.forEach((t) => {
          const id = t.id || t._id;
          if (!id || !t.name) return;
          map[t.name.toLowerCase()] = id;
        });
        setTemplatesByName(map);
      })
      .catch(() => setTemplatesByName({}));
  }, []);

  const resolveHref = (tile) => {
    if (tile.href) return tile.href;
    if (tile.matchName) {
      // Special case: ClearSky has its own dedicated submit flow inside its log page.
      if (tile.matchName === 'clearsky') {
        // ClearSky fill page works via the standard form fill route too
      }
      const needle = tile.matchName.toLowerCase();
      for (const [name, id] of Object.entries(templatesByName)) {
        if (name.includes(needle)) {
          return `/dashboard/forms/fill/${id}`;
        }
      }
    }
    // Fallback while templates load
    return '/dashboard/forms';
  };

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="member-dashboard">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          Hi {firstName}
        </h1>
        <p className="text-muted-foreground mt-1">
          Quick access to the things you use every day.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {MEMBER_TILES.map((tile) => {
          const Icon = tile.icon;
          const href = resolveHref(tile);
          return (
            <Link key={tile.label} to={href} data-testid={tile.testId}>
              <Card className="hover:border-primary/50 transition-colors cursor-pointer group h-full">
                <CardContent className="p-5 flex flex-col gap-3">
                  <div className={`${tile.color} w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {tile.label}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {tile.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
